/** @prettier */

import {isUndefined} from 'lodash';
import {useCallback, useEffect, useState} from 'react';
import * as apiIF from '../../Type/api';
import {
  getStatesSitesBySiteID,
  getStatesSitesFloors,
  getStatesSitesGroups,
  getStatesSitesSl,
} from '../../API/API_Collection';
import _ from 'lodash';

//指令画面で表示する項目の型宣言
type RunDataType = Pick<
  apiIF.resParameters.floorStates,
  | 'operation-mode'
  | 'air-flow'
  | 'run-stop'
  | 'indoor-temperature'
  | 'preset-temperature'
  | 'error-code'
>;

//ユニットの状態値から,指令画面で使う設定をピックアップするための一覧
const pickRunData = [
  'run-stop',
  'indoor-temperature',
  'preset-temperature',
  'operation-mode',
  'air-flow',
  'error-code',
];

/**
 * サイトの代表機の運転設定状態を取得するフック
 * @param siteID {string} サイトID
 * @param target {apiIF.reqParameters.postCommandsSitesQuery['mode'] | ''} 操作対象
 * @param targetID {string} 操作対象のID
 * @return サイトの代表機の運転設定状態
 */
export const useOperationStates = (
  siteID: string,
  target: apiIF.reqParameters.postCommandsSitesQuery['mode'] | '',
  targetID: string
) => {
  /** 指令・表示情報 */
  const [operationPower, setOperationPower] = useState(''); //運転停止 指令値

  /** 表示情報 */
  const [dispIndoorTemp, setDispIndoorTemp] = useState(''); //室内温度　表示
  const [dispPresetTemp, setDispPresetTemp] = useState(''); //設定温度　表示
  //prettier-ignore
  const [dispOpeMode, setDispOpeMode] = useState<apiIF.reqParameters.postCommandsSitesBody['operation-mode']>(); //運転モード　表示
  //prettier-ignore
  const [dispFlow, setDispFlow] = useState<apiIF.reqParameters.postCommandsSitesBody['air-flow']>(); //風量　表示
  const [dispError, setDispError] = useState(''); //エラー　表示

  //運転ボタンやAPPLYボタン押下時に、設定予定の指令値を現在値に上書きする関数
  //prettier-ignore
  const operationDataSet = useCallback((
    temperature: string,
    mode: apiIF.reqParameters.postCommandsSitesBody['operation-mode'],
    flow: apiIF.reqParameters.postCommandsSitesBody['air-flow']
  ) => {
    setDispPresetTemp(temperature);
    setDispOpeMode(mode);
    setDispFlow(flow);
  },[]);

  //運転ボタン押下時に運転停止表示を更新する関数
  const runDispDataSet = useCallback((run: string) => {
    setOperationPower(run);
  }, []);

  //APIで取得した状態値を,指令画面で表示する項目に反映する関数
  const statesSet = (runData: RunDataType) => {
    setOperationPower(runData['run-stop'] ?? '');
    setDispIndoorTemp(
      runData['indoor-temperature'] === undefined
        ? ''
        : runData['indoor-temperature']
    );
    setDispPresetTemp(
      runData['preset-temperature'] === undefined
        ? ''
        : runData['preset-temperature']
    );
    setDispOpeMode(runData['operation-mode']);
    setDispFlow(runData['air-flow']);
    setDispError(runData['error-code'] ?? '');
  };

  useEffect(() => {
    switch (target) {
      //指令対象がサイトの場合
      case 'site':
        //prettier-ignore
        let field = 'run-stop,indoor-temperature,preset-temperature,operation-mode,air-flow,error-code,register-number';
        //サイト代表機の状態値を取得
        getStatesSitesBySiteID(siteID, {
          fields: field,
        }).then(response => {
          if (isUndefined(response)) return;
          //取得した状態値から、指令画面で表示する項目を抽出する
          const runData: RunDataType = _.pick(
            response['states'][0]['airconditioner'],
            pickRunData
          );
          //表示に反映する
          statesSet(runData);
        });
        break;

      //指令対象がフロアの場合
      case 'floors':
        getStatesSitesFloors(siteID).then(response => {
          if (isUndefined(response)) return;
          //対象サイトの状態値を取得
          for (const state of Object.values(
            response['floor-states'][0]['states']
          )) {
            if (state['floor'] === targetID) {
              //取得した状態値から、指令画面で表示する項目を抽出する
              const runData: RunDataType = _.pick(state, pickRunData);
              //表示に反映する
              statesSet(runData);
              break; //データが見つかったらループを抜ける
            }
          }
        });
        break;

      //指令対象がグループの場合
      case 'groups':
        getStatesSitesGroups(siteID).then(response => {
          if (isUndefined(response)) return;
          //対象グループの状態値を取得
          for (const state of Object.values(
            response['group-states'][0]['states']
          )) {
            if (state['group-id'] === targetID) {
              //取得した状態値から、指令画面で表示する項目を抽出する
              const runData: RunDataType = _.pick(state, pickRunData);
              //表示に反映する
              statesSet(runData);
              break; //データが見つかったらループを抜ける
            }
          }
        });
        break;

      //指令対象がユニットの場合
      case 'sl':
        getStatesSitesSl(siteID).then(response => {
          if (isUndefined(response)) return;
          //対象ユニットの状態値を取得
          for (const state of Object.values(
            response['sl-states'][0]['states']
          )) {
            if (state['unit-id'] === targetID) {
              //取得した状態値から、指令画面で表示する項目を抽出する
              const runData: RunDataType = _.pick(state, pickRunData);
              //表示に反映する
              statesSet(runData);
              break; //データが見つかったらループを抜ける
            }
          }
        });
        break;
    }
  }, [siteID, target, targetID]);

  return {
    operationPower,
    dispIndoorTemp,
    dispPresetTemp,
    dispOpeMode,
    dispFlow,
    dispError,
    operationDataSet,
    runDispDataSet,
  };
};
